import { TrackedHandler } from './tracked-handler';
import { QueryCallback, TrackedHandlerList } from './types';

export enum IntersectionObserverStateActionNames {
    CREATE_OBSERVER = 'create-observer',
    DELETE_OBSERVER = 'delete-observer',
    RESET_HANDLERS = 'reset-handlers',
    SET_TOP_ELEMENT = 'set-top-element',
    TRACK_ELEMENT = 'track-element',
}

export interface IntersectionObserverState {
    observer: IntersectionObserver;
    topElement: HTMLElement;
    trackedHandlers: TrackedHandlerList;
}

interface CreateObserverAction {
    type: IntersectionObserverStateActionNames.CREATE_OBSERVER;
    callback: IntersectionObserverCallback;
    topElement: HTMLElement;
    options: {};
}

interface DeleteObserverAction {
    type: IntersectionObserverStateActionNames.DELETE_OBSERVER;
}

interface ResetHandlersAction {
    type: IntersectionObserverStateActionNames.RESET_HANDLERS;
}

interface TrackElementAction {
    type: IntersectionObserverStateActionNames.TRACK_ELEMENT;
    query: string;
    callback?: QueryCallback;
}

export type IntersectionObserverStateAction =
    | CreateObserverAction
    | DeleteObserverAction
    | ResetHandlersAction
    | TrackElementAction;

export const INITIAL_INTERSECTION_OBSERVER_STATE: IntersectionObserverState = {
    observer: null,
    topElement: null,
    trackedHandlers: {},
};

const createNewObserver = (state: IntersectionObserverState, action: CreateObserverAction): IntersectionObserver => {
    if (state.observer) {
        state.observer.disconnect();
    }

    return new IntersectionObserver(action.callback, {
        ...action.options,
        root: action.topElement.parentElement,
    });
};

const resetHandlers = (state: IntersectionObserverState): void => {
    Object.values(state.trackedHandlers).forEach((handler) => {
        handler.unobserve();
    });
};

export function intersectionObserverReducer(
    state: IntersectionObserverState,
    action: IntersectionObserverStateAction,
): IntersectionObserverState {
    switch (action.type) {
        case IntersectionObserverStateActionNames.CREATE_OBSERVER:
            if (state.observer && state.topElement && action.topElement.isSameNode(state.topElement)) {
                return state;
            }

            return {
                ...state,
                observer: createNewObserver(state, action),
                topElement: action.topElement,
            };

        case IntersectionObserverStateActionNames.DELETE_OBSERVER:
            if (state.observer) {
                resetHandlers(state);
                state.observer.disconnect();
            }

            return {
                ...state,
                observer: null,
                topElement: null,
                trackedHandlers: {},
            };

        case IntersectionObserverStateActionNames.RESET_HANDLERS:
            resetHandlers(state);

            return {
                ...state,
                trackedHandlers: {},
            };

        case IntersectionObserverStateActionNames.TRACK_ELEMENT:
            const newHandlers = { ...state.trackedHandlers };
            newHandlers[action.query] = new TrackedHandler(state.observer, state.topElement, {
                query: action.query,
                callback: action.callback,
            });

            return {
                ...state,
                trackedHandlers: newHandlers,
            };

        default:
            console.error('Unknown intersection observer state reducer action', action);
            return state;
    }
}
